import ImageCard from '@components/common/ImageCard';
import Icon from '@components/common/Icon';
import { getDataUrl } from '@lib/utils';

const recognitions = [
  {
    image: 'chess/recognitions/national_master_title_2023.webp',
    title: 'National Master Title',
    year: '2023',
    description: 'Official recognition of achieving National Master status in competitive chess.',
    featured: true
  },
  {
    image: 'chess/recognitions/paref_southridge_chess_champion.webp',
    title: 'PAREF Southridge Chess Champion',
    year: '2024',
    description: 'Recognized as the school chess champion, demonstrating excellence in academics and chess.',
    featured: false
  },
  {
    image: 'chess/recognitions/ncfp_special_recognition_oct2024.webp',
    title: 'NCFP Special Recognition',
    year: '2024',
    description: 'Special recognition from the National Chess Federation of the Philippines for outstanding contributions.',
    featured: false
  }
];

const RecognitionsSection = () => {
  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Official Recognition
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Certificates and official recognitions from chess federations and educational institutions.
          </p>
        </div>

        {/* Recognition Cards - Desktop: Grid, Mobile: Horizontal scroll */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {recognitions.map((recognition, index) => (
            <div
              key={index}
              className={`group rounded-xl border transition-all duration-300 hover:shadow-card-hover overflow-hidden ${
                recognition.featured ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: recognition.featured
                  ? 'var(--color-accent-6)'
                  : 'var(--color-card-border)',
                borderWidth: recognition.featured ? '2px' : '1px',
                boxShadow: recognition.featured
                  ? '0 10px 30px rgba(0,0,0,0.1)'
                  : 'var(--shadow-card)'
              }}
            >
              {/* Certificate Image */}
              <div className="relative overflow-hidden">
                {recognition.featured && (
                  <div
                    className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: 'var(--color-accent-6)',
                      color: 'var(--color-button-primary-text)'
                    }}
                  >
                    FEATURED
                  </div>
                )}
                <ImageCard
                  src={getDataUrl(recognition.image)}
                  alt={recognition.title}
                  aspectRatio="aspect-[4/3]"
                  rounded={false}
                  shadow={false}
                  className="group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: recognition.featured
                        ? 'var(--color-accent-1)'
                        : 'var(--color-theme-2)'
                    }}
                  >
                    <Icon
                      name="chess"
                      size="md"
                      style={{
                        color: recognition.featured
                          ? 'var(--color-accent-6)'
                          : 'var(--color-icon-default)'
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h3
                      className="text-lg font-bold"
                      style={{ color: 'var(--color-heading)' }}
                    >
                      {recognition.title}
                    </h3>
                    <p
                      className="text-sm font-medium"
                      style={{ color: 'var(--color-accent-6)' }}
                    >
                      {recognition.year}
                    </p>
                  </div>
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--color-card-text)' }}
                >
                  {recognition.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Horizontal Scroll */}
        <div className="md:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
          <div className="flex gap-6 pb-4">
            {recognitions.map((recognition, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-80 rounded-xl border overflow-hidden shadow-card"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: recognition.featured
                    ? 'var(--color-accent-6)'
                    : 'var(--color-card-border)',
                  borderWidth: recognition.featured ? '2px' : '1px',
                  boxShadow: recognition.featured
                    ? '0 10px 30px rgba(0,0,0,0.1)'
                    : 'var(--shadow-card)'
                }}
              >
                {/* Certificate Image */}
                <div className="relative overflow-hidden">
                  {recognition.featured && (
                    <div
                      className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold"
                      style={{
                        backgroundColor: 'var(--color-accent-6)',
                        color: 'var(--color-button-primary-text)'
                      }}
                    >
                      FEATURED
                    </div>
                  )}
                  <ImageCard
                    src={getDataUrl(recognition.image)}
                    alt={recognition.title}
                    aspectRatio="aspect-[4/3]"
                    rounded={false}
                    shadow={false}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: recognition.featured
                          ? 'var(--color-accent-1)'
                          : 'var(--color-theme-2)'
                      }}
                    >
                      <Icon
                        name="chess"
                        size="md"
                        style={{
                          color: recognition.featured
                            ? 'var(--color-accent-6)'
                            : 'var(--color-icon-default)'
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <h3
                        className="text-lg font-bold"
                        style={{ color: 'var(--color-heading)' }}
                      >
                        {recognition.title}
                      </h3>
                      <p
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-accent-6)' }}
                      >
                        {recognition.year}
                      </p>
                    </div>
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-card-text)' }}
                  >
                    {recognition.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div
          className="mt-12 p-6 rounded-xl border text-center"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-card-border)'
          }}
        >
          <p
            className="text-sm"
            style={{ color: 'var(--color-muted)' }}
          >
            All recognitions are official certifications from recognized chess federations and educational institutions.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RecognitionsSection;
